import styled from "styled-components";
import { useMutation, useQueryClient } from "react-query";
import { useRecoilState, useSetRecoilState } from "recoil";
import { MDBBadge } from "mdb-react-ui-kit";
import { format } from "timeago.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useDecision } from "@optimizely/react-sdk";
import { useTranslation } from "react-i18next";
import { Button, ButtonColumn } from "../App.styles";
import { ConfirmationModal, ExternalLinkButton, LinkButton as RouterLinkButton, Spinner } from "../common";
import { useCollectReviewsMutation, termState, useLiberateMutation } from "./Dashboard.state";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";
import { UpdatedAt, DeleteButton, Td } from "./Dashboard.styles";
import LiberateDropdown from "./LiberateDropdown";
import { axios } from "../lib/request";
import { dashboardQueryKey } from "../lib/refresh";
import usePreviewButton from "../hooks/usePreviewButton";
import { pageState } from '../monitor/Monitor.state'
import { pageState as dashboardPageState } from "./Dashboard.state";
import useResetOpenStates from "../lib/useResetOpenStates";
import { useUserByGameIdQuery } from "../monitor/User.state";
import withRealTimeDot from "../hoc/withRealTimeDot";

const HighlightId = styled.span`
  padding: 5px;
`;

const CategoryBadge = styled(MDBBadge)`
  margin-top: 1rem;
`;

const color = (category) => {
  switch (category) {
    case "Active":
    default:
      return "success";
    case "Template":
      return "warning";
    case "Draft":
      return "danger";
    case "Archive":
      return "info";
  }
};

const LinkButton = withRealTimeDot(RouterLinkButton)

const GameSummary = ({
  code,
  export_player_enabled,
  id,
  name,
  updated_at,
  admin,
  category,
  user,
  liberated,
  purchased_players,
  onDelete,
  collect_reviews,
}) => {
  const { game: gameRole } = useUserByGameIdQuery(id);
  const { administrator, coordinator, super_admin } = gameRole || {};
  const { t, i18n } = useTranslation();
  const [decisionDonations] = useDecision('donations');
  const [decisionGroups] = useDecision('groups');
  const [decisionAnalytics] = useDecision('analytics');
  const [decisionAdventures] = useDecision('adventures')
  const [decisionNpc] = useDecision('npc')
  const [decisionDesignations] = useDecision('designations')
  const [decisionCollectReviews] = useDecision('collect-reviews')
  const queryClient = useQueryClient();
  const setAlert = useSetRecoilState(alertState);
  const setDashboardPage = useSetRecoilState(dashboardPageState);
  const setTerm = useSetRecoilState(termState);
  const [, setMonitorPage] = useRecoilState(pageState);
  const { isLiberating, liberate } = useLiberateMutation(id);
  const { isRequestingReviews, requestReviews } = useCollectReviewsMutation(id);
  const { isLoading, mutate } = useMutation(
    () => axios.post(`${process.env.REACT_APP_API_CLONE_URL}/${id}/clone.json`),
    {
      onSettled: () => window.scrollTo({ top: 0, behavior: "smooth" }),
      onSuccess: () => {
        setAlert({
          type: "info",
          message:
            "Your private game has been created. Go ahead and fine tune it, then get your phone and get going!",
        });
        setDashboardPage(1);
        setTerm('');
        queryClient.invalidateQueries(dashboardQueryKey());
      },
      onError: () => {
        setAlert({
          type: "error",
          message:
            "Cloning Failed. Your clone was unsuccessful. Please try again or contact support.",
        });
      }
    }
  );
  const onLiberate = (pkg, count) => liberate({
    params: {
      packages: pkg,
      count,
    } 
  })
  const previewBtnProps = usePreviewButton({ code })
  const resetOpenState = useResetOpenStates()
  const LinkWithResetState = ({ to, children, gameId, ...rest }) => {
    return (
      <LinkButton to={to} gameId={gameId} {...rest} onClick={resetOpenState}>
        {children}
      </LinkButton>
    )
  }

  return (
    <tr className={`game-summary game-summary--${id}`}>
      <Td>
        <HighlightId className="alert alert-success">{id}</HighlightId>
        <CategoryBadge color={color(category)}>{category}</CategoryBadge>
      </Td>
      <Td>{name}</Td>
      <Td>{code}</Td>
      <Td>
        <UpdatedAt title={updated_at}>{format(updated_at, i18n.language)}</UpdatedAt>
      </Td>
      <Td>
        {admin && (
          <ButtonColumn>
            <LinkButton color="primary" to={`/games/edit/${id}`} gameId={id}>
              {t('Settings')}
            </LinkButton>
            <LinkWithResetState data-testid={`nav--challenge--${id}`} color="primary" to={`/tasks/organize/${id}`} gameId={id}>
              {t('Challenges')}
            </LinkWithResetState>
            {
              decisionNpc?.enabled
                ? (
                  <LinkWithResetState color="primary" to={`/games/npc/${id}`} gameId={id}>
                    {t('Non Player Characters')}
                  </LinkWithResetState>
                )
                : null
            }
            {
              decisionAdventures?.enabled
                ? (
                  <LinkButton color="primary" to={`/games/path/${id}`} gameId={id}>
                    {t('Path')}
                  </LinkButton>
                )
                : null
            }
            {
              decisionDesignations?.enabled
                ? (
                  <LinkWithResetState color="primary" to={`/games/designations/${id}`} gameId={id}>
                    {t('Designations')}
                  </LinkWithResetState>
                )
                : null
            }
            <LinkButton color="primary" to={`/tasks/library/${id}`} gameId={id}>
              {t('Library')}
            </LinkButton>
            <LinkButton color="primary" to={`/teams/organize/${id}`} gameId={id}>
              {t('Teams')}
            </LinkButton>
            {
              decisionGroups?.enabled
                ? (
                  <LinkButton color="primary" to={`/groups/organize/${id}`} gameId={id}>
                    {t('Groups')}
                  </LinkButton>
                )
                : null
            }
            <LinkButton color="primary" to={`/games/rewards/${id}`} gameId={id}>
              {t('Rewards')}
            </LinkButton>
            <LinkButton color="primary" to={`/games/grey/${id}`} gameId={id}>
              {t('Branding')}
            </LinkButton>
            <LinkButton color="primary" to={`/games/personalizations/${id}`} gameId={id}>
              {t('Personalizations')}
            </LinkButton>
            {user.super_admin && (
              <LiberateDropdown 
                onLiberate={onLiberate}
                isLiberating={isLiberating}
                liberated={liberated}
                purchased={purchased_players}
              />
            )}
            <ExternalLinkButton
              color="warning"
              {...previewBtnProps}
              href={undefined}
              target={undefined}
            >
              {t('App Preview')}
            </ExternalLinkButton>
          </ButtonColumn>
        )}
      </Td>
      <Td>
        <ButtonColumn>
          <LinkButton color="primary" to={`/games/stream/${id}`} gameId={id}>
            {t('Stream')}
          </LinkButton>
          <LinkButton
            color="primary"
            to={`/teams/leaderboard/${id}?style="alltime|daily|weekly"`}
            gameId={id}
          >
            {t('Leaderboard')}
          </LinkButton>
          {admin && (
            <LinkButton 
                color="primary" 
                to={`/answers/monitor/${id}`}
                onClick={() => {
                    // https://github.com/facebookexperimental/Recoil/issues/1975
                    // recoil-sync is not resetting when URL params are not present.
                    setMonitorPage(1)
                }}
                gameId={id}
            >
              {t('Monitor')}
            </LinkButton>
          )}
          {
            decisionAnalytics?.enabled && admin
              ? (
                <LinkButton color="primary" to={`/games/analytics/${id}`} gameId={id}>
                  {t('Analytics')}
                </LinkButton>
              )
              : null
          }
        </ButtonColumn>
      </Td>
      <Td>
        <ButtonColumn>
          {(admin || export_player_enabled) && (
            <LinkButton color="primary" to={`/games/exports/${id}`} gameId={id}>
              {t('Exports')}
            </LinkButton>
          )}
          {
            decisionCollectReviews?.enabled &&
            collect_reviews === true &&
            (user?.super_admin || admin)
                ? (
                    <ConfirmationModal
                        title={t("Ask for Reviews?")}
                        body={t(`If the game has finished, you can ask players to leave a Google review. 
                        Each time you request reviews, an email will be sent. 
                        You should only ask for reviews once.`)}
                        confirmLabel={t("Yes, Ask for Reviews")}
                        confirmAction={requestReviews}
                        isLoading={isRequestingReviews}
                    >
                        <Button color="primary">
                            {t('Ask for Reviews')}
                        </Button>
                    </ConfirmationModal>
                )
                : null
          }
          <LinkButton color="warning" to={`/games/slideshow/${id}`} gameId={id}>
            {t('Slideshow')}
          </LinkButton>
          {(administrator || super_admin || coordinator) && (
            <>
              <LinkButton color="warning" to={`/games/reels/${id}`} gameId={id}>
                {t('Reels')}
              </LinkButton>
              {
                decisionDonations?.enabled
                  ? (
                    <LinkButton color="warning" to={`/games/donate/${id}`} gameId={id}>
                      {t('Donations')}
                    </LinkButton>
                  )
                  : null
              }
              <Button color="accent1" onClick={() => mutate(id)}>
                {isLoading ? <Spinner size="0.875rem" /> : t('Clone')}
              </Button>
            </>
          )}
        </ButtonColumn>
      </Td>
      <Td>{user.access}</Td>
      <Td>
        {admin && (
          <DeleteButton color="danger" onClick={onDelete}>
            <FontAwesomeIcon icon={faTimes} />
          </DeleteButton>
        )}
      </Td>
    </tr>
  );
};

export default GameSummary;
